// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.

import { useEffect, useRef } from 'react';

type LevelContent = {
  level: string;
  title: string;
  description: string;
};

const LEVELS_DATA: LevelContent[] = [
  {
    level: '1',
    title: 'Level 1 — AI Aware',
    description: 'Understands what AI is, why it matters, and can describe its basic concepts and societal impact.'
  },
  {
    level: '2',
    title: 'Level 2 — AI Literate',
    description: 'Uses AI tools for simple tasks with basic understanding of prompting, risks, and output evaluation.'
  },
  {
    level: '3',
    title: 'Level 3 — AI Practitioner',
    description:
      'Integrates AI into regular workflows, builds small automations, and selects appropriate tools for specific use cases.'
  },
  {
    level: '4',
    title: 'Level 4 — AI Fluent',
    description:
      'Designs and builds production-grade AI solutions, understands architecture trade-offs, and quantifies business impact.'
  },
  {
    level: '5',
    title: 'Level 5 — AI Expert',
    description:
      'Architects, optimizes, and governs enterprise-scale AI systems with deep technical mastery and end-to-end lifecycle ownership.'
  }
];

export interface LevelsInfoPopupProps {
  onClose: () => void;
}

export const LevelsInfoPopup = ({ onClose }: LevelsInfoPopupProps): JSX.Element => {
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  useEffect(() => {
    modalRef.current?.focus();
  }, []);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="levels-info-title"
      aria-describedby="levels-info-description"
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0,0,0,0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1300
      }}
      onClick={onClose}
    >
      <div
        ref={modalRef}
        tabIndex={-1}
        style={{
          background: '#fff',
          borderRadius: 24,
          maxWidth: 880,
          width: '92%',
          maxHeight: '90vh',
          boxShadow: '0 24px 80px rgba(15,23,42,0.45)',
          overflow: 'hidden',
          fontFamily: 'G42 Sans',
          display: 'flex',
          flexDirection: 'column'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          id="levels-info-title"
          style={{
            background: '#455391',
            color: '#fff',
            padding: '20px 24px 18px',
            flexShrink: 0
          }}
        >
          <div style={{ fontSize: 20, fontWeight: 500 }}>What each AI Skill Level means</div>
          {/* <p id="levels-info-description" style={{ margin: '8px 0 0', fontSize: 13, opacity: 0.9 }}>
            Choose the level that best matches your current AI skills.
          </p> */}
        </div>

        <div style={{ padding: '0 22px 20px', overflowY: 'auto', flex: 1 }}>
          <p
            style={{
              margin: '24px 0 16px',
              fontSize: 18,
              color: '#00d2aa',
              fontWeight: 500
            }}
          >
            Most users start with Level 2 (AI Literate)
          </p>
          {LEVELS_DATA.map((level) => (
            <section key={level.level} aria-labelledby={`level-${level.level}-title`} style={{ marginBottom: 20 }}>
              <div
                id={`level-${level.level}-title`}
                style={{
                  fontSize: 16,
                  fontWeight: 500,
                  color: '#2A312B',
                  marginBottom: 6
                }}
              >
                {level.title}
              </div>
              <p style={{ margin: 0, fontSize: 14, color: 'rgba(42, 49, 43, 0.85)', lineHeight: 1.5 }}>
                {level.description}
              </p>
            </section>
          ))}

          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                borderRadius: 999,
                border: 'none',
                padding: '9px 18px',
                fontSize: 14,
                fontWeight: 600,
                backgroundColor: '#00D2AA',
                color: '#111827',
                cursor: 'pointer'
              }}
              aria-label="Close dialog"
            >
              Got it
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
